import { Box, Checkbox, FormControl,  FormHelperText, Input, InputLabel, MenuItem, Select, TextField } from '@mui/material'
import { object, string, InferType, array, date, mixed } from 'yup'
import { useFormContext } from '../context/FormContext'
import { useMemo, useRef, useState } from 'react'
import CloseIcon from '@mui/icons-material/Close';

export const formatDateString = (date: Date) => {
    return `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}/${date.getFullYear()}`
}

export const formatDateString_en = (date: Date) => {
    return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`
}

const listPositions = {
'AB': {
    label_en: 'AB',
    label_de: 'Matros*in'
},
'Bosun': {
    label_en: 'Bosun',
    label_de: 'Bosun'
},
'Captain': {
    label_en: 'Captain',
    label_de: 'Kapitän*in'
},
'Care Co': {
    label_en: 'Care coordinator',
    label_de: 'Care Koordinator*in'
},
'Chief Engineer': {
    label_en: 'Chief engineer',
    label_de: 'Erste*r Maschinist*in'
},
'Chief Mate': {
    label_en: 'Chief officer',
    label_de: 'Erste*r Offizier*in'
},
'Communication': {
    label_en: 'Communication coordinator',
    label_de: 'Koordinator*in für Kommunikation'
},
'Cook': {
    label_en: 'Cook',
    label_de: 'Köch*in'
},
'Crew Change Cook': {
    label_en: 'Crew change cook',
    label_de: 'Crewwechsel Köch*in'
},
'Cultural Mediator': {
    label_en: 'Cultural mediator',
    label_de: 'Kulturvermittler*in'
},
'Doctor': {
    label_en: 'Doctor',
    label_de: 'Ärzt*in'
},
'Electrician': {
    label_en: 'Electrician and IT specialist',
    label_de: 'Elektriker*in'
},
'Engine Rating': {
    label_en: 'Third engineer',
    label_de: 'Dritte*r Maschinist*in'
},
'Human Rights Observer': {
    label_en: 'Human rights observer',
    label_de: 'Beauftragte*r für Menschenrechte'
},
'Journalist': {
    label_en: 'Journalist',
    label_de: 'Journalist*in'
},
'Logistics Onshore': {
    label_en: 'Logistics onshore',
    label_de: 'Logistik an Land'
},
'Mental Health': {
    label_en: 'Mental health representative',
    label_de: 'Psycholog*in'
},
'Midwife': {
    label_en: 'Midwife',
    label_de: 'Hebamme'
},
'Paramedic/Nurse': {
    label_en: 'Paramedic / nurse',
    label_de: 'Sanitäter*in / Pfleger*in'
},
'Photographer': {
    label_en: 'Photographer',
    label_de: 'Fotograf*in'
},
'Protection': {
    label_en: 'Protection',
    label_de: 'Schutzbeauftragte*r'
},
'RHIB Driver': {
    label_en: 'RHIB driver',
    label_de: 'RHIB Fahrer*in'
},
'RHIB Lead': {
    label_en: 'RHIB lead',
    label_de: 'RHIB Leiteung'
},
'RHIB Support': {
    label_en: 'RHIB support',
    label_de: 'RHIB Unterstützung'
},
'SAR Co': {
    label_en: 'SAR coordinator',
    label_de: 'SAR-Koordinator*in'
},
'SAR Member': {
    label_en: 'SAR member',
    label_de: 'SAR-Mitglied'
},
'Shipyard Worker': {
    label_en: 'Shipyard worker',
    label_de: 'Werftarbeiter*in'
},
'2nd Engineer': {
    label_en: 'Second engineer',
    label_de: 'Zweite*r Maschinist*in'
},
'2nd Mate': {
    label_en: 'Second officer',
    label_de: 'Zweite*r Offizier*in'
},
} // TODO get from post_type position

export const positionSchema = object({
    positions: array().of(string()).min(1, 'Bitte wähle mindestens eine Position aus.'),
    startDate: date().required('Bitte wähle ein Datum aus.'),
    moreInfoDate: string(),
    cv: mixed().required('Bitte laden Sie ihren Lebenslauf hoch.').test('fileSize', 'Die Datei ist zu groß. Bitte wähle eine Datei kleiner als 5MB.', (value) => {
        if (value instanceof File && value.size) {
            return value.size <= 5000000
        }
        return true
    }),
    motivation: string().test('three-sentences', 'Bitte schreibe mindestens drei Sätze!', (value) => {
        if (value) return value.split(/[.!?]/).length > 3
        return false
    }),
})

export const positionSchema_en = object({
    positions: array().of(string()).min(1, 'Please select at least one position.'),
    startDate: date().required('Please select a starting date.'),
    moreInfoDate: string(),
    cv: mixed().required('Please provide a CV.').test('fileSize', 'The file is too large, please select a file smaller than 5MB.', (value) => {
        if (value instanceof File && value.size) {
            return value.size <= 5000000
        }
        return true
    }),
    motivation: string().test('three-sentences', 'Please write at least three sentences!', (value) => {
        if (value) return value.split(/[.!?]/).length > 3
        return false
    }),
})

export interface PositionData extends Omit<InferType<typeof positionSchema>, 'startDate' | 'cv'> {
    startDate: Date | ''
    cv: File | undefined
}

const PositionScreen = () => {

    const [multiSelectOpen, setMultiSelectOpen] = useState(false)

    const {
        formData, formik, labels, lang
    } = useFormContext()

    const {
        positions,
        startDate,
        moreInfoDate,
        cv,
        motivation,
    } = formData


    const [textareaFocused, setTextareaFocused] = useState(false)
    const positionsList = useMemo(() => {
        if (lang === 'de') {
            return Object.keys(listPositions).map((pos) => ({ key: pos, label: listPositions[pos as keyof typeof listPositions].label_de }))
        } else {
            return Object.keys(listPositions).map((pos) => ({ key: pos, label: listPositions[pos as keyof typeof listPositions].label_en }))
        }
    },
    [lang])

    const cvInputRef = useRef<HTMLInputElement>(null)

    if (!formik) {
        return null
    }

    const { handleChange, handleBlur, touched, errors, setFieldValue } = formik

    return (
        <Box width="100%" className="screen-position">
            <div className="react-form-title">
                <h2>{labels.position.title}</h2>
            </div>
            <FormControl sx={{ width: '100%', height: '70px', '@media (min-width: 1024px)': { height: '90px', width: 'calc(50% - 20px)' },
                }} variant="standard">
                <InputLabel id="positions-label"
                    sx={{
                        color: touched.positions && Boolean(errors.positions) ? 'var(--error)' : 'rgba(0, 0, 0, 0.6)',
                    }}
                >{labels.position.positions}</InputLabel>
                <Select
                    id="positions"
                    label={labels.position.positions}
                    labelId="positions-label"
                    value={positions}
                    onBlur={handleBlur}
                    onChange={(e) => {
                        const data = e.target.value as (string | undefined)[]
                        const filteredData = data.filter((value) => value !== undefined)
                        setFieldValue('positions', filteredData)

                    }}
                    error={touched.positions && Boolean(errors.positions)}
                    name='positions'
                    open={multiSelectOpen}
                    onOpen={() => setMultiSelectOpen(true)}
                    onClose={() => setMultiSelectOpen(false)}
                    variant="standard"
                    multiple
                    fullWidth
                    sx={{paddingTop: '6px', '.MuiSelect-select' : {'@media (min-width: 1024px)': {display: 'flex', flexDirection: 'column', justifyContent: 'center' }}}}
                    native={false}
                    renderValue={(selected) => <>{selected.map(s =>  s && positionsList?.find(p => p.key === s )?.label).join(', ')}</> }
                >
                    <MenuItem
                        value={undefined}
                        sx={{ fontSize: '16px' }}
                        onClick={(e) => {
                            e.stopPropagation()
                            setMultiSelectOpen(false)
                        }}
                    >
                        {labels.general.close}
                    </MenuItem>

                    {positionsList.map((pos) => {
                        return (
                            <MenuItem  
                                key={pos.key} 
                                value={pos.key}
                                sx={{ fontSize: '16px' }}
                            >   
                                <Checkbox checked={Boolean(positions?.find(value => value === pos.key))} />
                                {pos.label}
                            </MenuItem>
                    )})}
                </Select>
                <FormHelperText sx={{ mx: 0, transform: 'unset', color: 'var(--error)' }} >{touched.positions && errors.positions}</FormHelperText>
            </FormControl>
            <Box sx={{'@media (min-width: 1024px)': { display: 'flex', height: '94px', justifyContent: 'space-between', alignItems: 'flex-start', marginTop: '10px', gap: '40px' }}}>
                <Box sx={{ height: '70px', width: '100%', marginTop: '20px', '@media (min-width: 1024px)': {  height: '80px', marginTop: '0' }}}>
                    <TextField
                        id="startDate"
                        label={labels.position.startDate}
                        value={startDate ? formatDateString_en((new Date(startDate))) : ''}
                        onFocus={(e) => { const input = e.target as HTMLInputElement; input.type = 'date'; input.pattern = "yyyy-MM-dd"; input.focus(); input.pattern = "yyyy-MM-dd"; input.showPicker(); }}
                        onBlur={(e) => { const input = e.target as HTMLInputElement; input.type = 'text'; handleBlur(e) }}
                        onChange={handleChange}
                        error={touched.startDate && Boolean(errors.startDate)}
                        helperText={touched.startDate && errors.startDate && String(errors.startDate)}
                        name='startDate'
                        variant="standard"
                        fullWidth
                        sx={{ 'input' : {lineHeight: '23px'}}}
                    />
                </Box>
                <Box sx={{ width: '100%', marginTop: '20px', '@media (min-width: 1024px)': { marginTop: '0' }}}>
                    <TextField
                        id="moreInfoDate"
                        inputProps={{ maxLength: 100 }}
                        label={labels.position.moreInfoDate}
                        value={moreInfoDate}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        error={touched.moreInfoDate && Boolean(errors.moreInfoDate)}
                        helperText={touched.moreInfoDate && errors.moreInfoDate}
                        name='moreInfoDate'
                        variant="standard"
                        fullWidth
                    />
                </Box>
            </Box>
            <Input 
                type="file"
                id="cv"
                name="cv"
                ref={cvInputRef}
                onChange={(e) => {
                    const event = e as React.ChangeEvent<HTMLInputElement>
                    const f = event.target.files?.[0]
                    setFieldValue('cv', f)
                }}
                inputProps={{
                    accept: "application/pdf"
                }}
                style={{display: 'none'}}
            />
            <TextField
                id="cvName"
                label={labels.position.cv}
                value={cv?.name || ''}
                onFocus={(e) => {
                    const i = cvInputRef.current?.querySelector('input')
                    e.target.blur()
                    i?.click()
                }}
                onBlur={handleBlur}
                error={touched.cv && Boolean(errors.cv)}
                helperText={touched.cv && errors.cv}
                name='cvName'
                variant="standard"
                sx={{ marginBottom: '20px',  cursor: 'pointer', '@media (min-width: 1024px)': { width: 'calc(50% - 20px)', marginTop: '0' }}}                
                InputProps={{
                    endAdornment: cv?.name ? (<Box component="span" sx={{ cursor: 'pointer', color: 'rgba(0, 0, 0, 0.6)', fontSize: '16px', display: 'flex', alignItems: 'center' }} onClick={() => setFieldValue('cv', undefined)}><CloseIcon /></Box>) : null
                }}
            />
            <TextField
                id='motivation'
                label={labels.position.motivation}
                placeholder={labels.position.motivationPlaceholder}
                value={motivation}
                onFocus={() => setTextareaFocused(true)}
                onBlur={(e) => {setTextareaFocused(false); handleBlur(e)}}
                onChange={handleChange}
                error={touched.motivation && Boolean(errors.motivation)}
                helperText={(touched.motivation && errors.motivation) || ' '}
                name='motivation'
                variant="standard"
                fullWidth
                multiline
                rows={8}
                sx={{
                    height: '187px',
                    marginTop: '20px',
                    '@media (min-width: 1024px)': { marginTop: '0' },
                    'textarea': { backgroundColor: 'rgba(220, 217, 211, 1)', padding: '16px', '@media (min-width: 1024px)': { marginTop: '5px' } },
                    'label': { 
                        zIndex: 1, 
                        padding: textareaFocused ? '0' : motivation ? 0 : '14px', 
                        transform: motivation && 'translate(0, 0px) scale(0.75)', 
                    },
                    '&.MuiFormControl-root': { height: 'unset' },
                }}
            />
        </Box>)
}

export default PositionScreen